import { notEmpty } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import window from 'ember-window-mock';
import { resolve, reject } from 'rsvp';
import DS from 'ember-data';
import { storageFor } from 'ember-local-storage';
import { setMasqueradingContext } from 'cio/sentry';

import AccountService from 'cio/services/account';
import RouterService from '@ember/routing/router-service';
import SessionService from 'cio/services/session';
import AuthService from 'cio/services/auth';
import { CioSettingsLocalStorageType } from 'cio/storages/cio-settings';
import AuthenticatedController from 'cio/pods/authenticated/controller';
import ApiService from 'cio/services/api';
import { tracked } from '@glimmer/tracking';

export const ADMIN_ACCOUNT_ID = 1;

export default class MasqService extends Service {
  @service store!: DS.Store;
  @service session!: SessionService;
  @service account!: AccountService;
  @service auth!: AuthService;
  @service router!: RouterService;
  @service api!: ApiService;

  authenticatedController!: AuthenticatedController;

  @storageFor('cio-settings') localStorage!: CioSettingsLocalStorageType;

  @computed('account.current.admin', 'localStorage.showAdmin', 'isOn')
  get isAllowed() {
    if (!this.localStorage.get('showAdmin')) {
      return false;
    }

    return (this.account?.current?.admin && (this.auth?.user?.canMasquerade ?? false)) || this.isOn;
  }

  @notEmpty('masqueradedAs') isOn!: boolean;
  @tracked readOnly = false;

  @computed('account.current.{isMasqueraded,id}')
  get masqueradedAs() {
    return this.account.current?.isMasqueraded ? this.account.current?.id : null;
  }

  // main masq service initialization function:
  initializeAfterAccountAvailable() {
    let admin = false;
    let { masqueradedAs } = this;

    // initialize showAdmin to true for the admin account or if already masquerading as another account
    // false for other accounts
    if (typeof this.showAdmin !== 'boolean') {
      this.set('showAdmin', Boolean(admin || masqueradedAs));
    }

    if (this.masqueradedAs) {
      this.set('readOnly', true); // always default to read-only masqing - making any change will need an extra user action (flipping "read-only" switch in masq banner)
    }

    setMasqueradingContext(this.isOn);
  }

  // sets ?masq_account_id query param to correct value, listens to route changes to maintain its state
  initializeAfterAuthenticatedControllerReady(controller: AuthenticatedController) {
    if (!this.masqueradedAs) {
      controller.set('masqAccountID', null);
      return;
    }

    // make sure "?masq_account_id=..." query param is always up-to-date:
    // - environment routes, /env/123/... - not needed, environment implies account ID
    // - non-env routes, e.g. /settings - query param needed to load proper account after page refresh
    this.router.on('routeDidChange', () => {
      controller.set('masqAccountID', this.router.isActive('environments') ? null : this.account.current.id);
    });
  }

  // This controls whether the masquerade banner is visible or not
  @computed('localStorage.showAdmin')
  get showAdmin() {
    return this.localStorage.get('showAdmin');
  }

  set showAdmin(value) {
    this.localStorage.set('showAdmin', value);
  }

  toggleAdmin() {
    this.toggleProperty('showAdmin');
  }

  toggleReadOnly() {
    this.toggleProperty('readOnly');
  }

  masqueradeInto({ accountID }: { accountID: string }) {
    if (!accountID) {
      return reject();
    }

    if (this.router.isActive('environments')) {
      window.location.href = `/?masq_account_id=${accountID}`;
      return resolve();
    }

    let url = new URL(this.router.currentURL, window.location.origin);
    url.searchParams.set('masq_account_id', accountID);
    window.location.href = String(url);
    return resolve();
  }

  unmasquerade() {
    if (this.router.isActive('environments')) {
      // 1. Masqing in /env/... route:
      // redirect to root route to load default original user environment; also removing masq query params
      this.set('showAdmin', false);
      window.location.href = '/';
    } else {
      // 2. Masqing in non-/env/... routes:
      // remove masq query params, refresh without losing URL
      let url = new URL(this.router.currentURL, window.location.origin);
      url.searchParams.delete('masq_account_id');

      window.location.href = String(url);
    }
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    masq: MasqService;
  }
}
